/**
 * Modal Helper Utilities
 * 
 * Centralized utilities for opening Foundation modals with AJAX content.
 * Reduces duplication of the pattern: $.ajax() -> load HTML -> open modal
 */

import $ from 'jquery';

/**
 * Opens a modal and loads content from a URL via AJAX
 * 
 * @param {string} modalId - The ID of the modal element (without #)
 * @param {string} url - The URL to fetch content from
 * @param {object} data - Optional data to send with the AJAX request
 * @param {object} options - Optional Foundation reveal options
 * @returns {Promise} The jQuery AJAX promise
 * 
 * @example
 * openModalWithAjax('accept-modal', '/participants/accept', { id: 123 });
 */
export function openModalWithAjax(modalId, url, data = {}, options = {}) {
    return $.ajax({
        url,
        data,
        success: (response) => {
            const $modal = $(`#${modalId}`);
            $modal.html(response);
            $modal.foundation('reveal', 'open', options);
        },
        error: (xhr, status, error) => {
            console.error(`Failed to load modal content from ${url}:`, error);
            // Optionally show an error modal or notification
            showErrorNotification('Failed to load content. Please try again.');
        }
    });
}

/**
 * Opens a modal with specific content selector populated
 * Common pattern: modal has a -body element that gets filled
 * 
 * @param {string} modalId - The ID of the modal element (without #)
 * @param {string} contentSelector - The selector within the modal to populate (e.g., '#accept-modal-body')
 * @param {string} url - The URL to fetch content from
 * @param {object} data - Optional data to send with the AJAX request
 * @param {function} callback - Optional callback to execute after content is loaded
 * @returns {Promise} The jQuery AJAX promise
 * 
 * @example
 * openModalWithBody('accept-modal', '#accept-modal-body', '/participants/accept', { id: 123 });
 * 
 * // With callback
 * openModalWithBody('link-issue', '#link-issue', '/issues/link', {}, (response) => {
 *   // Do something after content is loaded
 *   $('#someField').val('value');
 * });
 */
export function openModalWithBody(modalId, contentSelector, url, data = {}, callback = null) {
    return $.ajax({
        url,
        data,
        success: (response) => {
            $(contentSelector).html(response);
            $(`#${modalId}`).foundation('reveal', 'open');

            // Execute callback if provided
            if (callback && typeof callback === 'function') {
                callback(response);
            }
        },
        error: (xhr, status, error) => {
            console.error(`Failed to load modal content from ${url}:`, error);
            showErrorNotification('Failed to load content. Please try again.');
        }
    });
}

/**
 * Opens a modal with content from an existing element on the page
 * 
 * @param {string} modalId - The ID of the modal element (without #)
 * @param {string} contentSourceSelector - The selector of the element containing content to show
 * @param {object} options - Optional Foundation reveal options
 * 
 * @example
 * openModalWithContent('confirmation-modal', '#hidden-confirmation-content');
 */
export function openModalWithContent(modalId, contentSourceSelector, options = {}) {
    const $modal = $(`#${modalId}`);
    const $content = $(contentSourceSelector);

    $modal.html($content.html());
    $modal.find($content).show();
    $modal.foundation('reveal', 'open', options);
}

/**
 * Closes a modal
 * 
 * @param {string} modalId - The ID of the modal element (without #)
 * 
 * @example
 * closeModal('accept-modal');
 */
export function closeModal(modalId) {
    $(`#${modalId}`).foundation('reveal', 'close');
}

/**
 * Helper to show error notifications
 * Can be enhanced to use a toast library or custom notification system
 */
function showErrorNotification(message) {
    // Simple alert for now - can be replaced with a nicer notification system
    if (window.showNotification) {
        window.showNotification(message, 'error');
    } else {
        alert(message);
    }
}
